/**
 * Avatar Component - shadcn/ui Style
 * ==================================
 * User avatar with fallback support.
 */

import * as React from "react";

import * as AvatarPrimitive from "@radix-ui/react-avatar";

import { cn } from "@/lib/utils";

const Avatar = React.forwardRef(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn(
      "relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full ring-2 ring-background",
      className,
    )}
    {...props}
  />
));
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = React.forwardRef(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn("aspect-square h-full w-full object-cover", className)}
    {...props}
  />
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const AvatarFallback = React.forwardRef(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      "flex h-full w-full items-center justify-center rounded-full bg-muted text-muted-foreground font-medium text-sm",
      className,
    )}
    {...props}
  />
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

// Squad-colored avatar fallback
const SquadAvatar = ({ squadId, icon: Icon, className, ...props }) => {
  const squadColors = {
    art: "bg-pink-100 text-pink-600 dark:bg-pink-900/40 dark:text-pink-300",
    coding: "bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300",
    text: "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-300",
    video:
      "bg-purple-100 text-purple-600 dark:bg-purple-900/40 dark:text-purple-300",
    general:
      "bg-indigo-100 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-300",
  };

  return (
    <Avatar className={className} {...props}>
      <AvatarFallback
        className={cn(squadColors[squadId] || squadColors.general)}
      >
        {Icon ? <Icon size={20} /> : <span className="text-lg">🤖</span>}
      </AvatarFallback>
    </Avatar>
  );
};

export { Avatar, AvatarImage, AvatarFallback, SquadAvatar };
